<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUpdated, computed } from "vue";
import { useI18n } from 'vue-i18n';
import { ChatUserType } from '@/enums/ChatUserType';
import PictureInPictureVideo from '@/components/Video/PictureInPictureVideo.vue';
import ChatMessage from '@/components/Chat/ChatMessage.vue';
import UploadingMessage from '@/components/Chat/UploadingMessage.vue';
import { BaseCircleButton } from '@/components/Base';
import { useChat } from '@/composables/useChat';
import { useVideo } from '@/composables/useVideo';
import { useSnapshot } from "@/composables/useSnapshot";

const { t } = useI18n();

const loading = ref(false);
const initialScrollDone = ref(false);

const {
  messages,
  translationsAllowed,
  dispatcherChatTextMessagePreview,
  sendMessage,
  sendPreview,
} = useChat();

const {
  wasActivated: videoIsAllowed,
  cameraStream
} = useVideo();

const {
  pendingUploads,
  sendSnapshot
} = useSnapshot();

const isUploading = computed(() => pendingUploads.value.length > 0);

const emit = defineEmits<{
  messageReceived: []
  pictureInPictureVideoClicked: []
}>();

const endRef = ref<HTMLDivElement | null>(null);

const scrollToBottom = (smooth = false) => {
  if (!endRef.value) return;
  endRef.value.scrollIntoView({
    behavior: smooth ? 'smooth' : 'auto',
    block: 'end',
  });
};

onMounted(async () => {
  await nextTick();
  scrollToBottom(false);

  if (messages.value.length > 0) {
    emit('messageReceived');
  }

  watch(
    [dispatcherChatTextMessagePreview, messages, pendingUploads],
    async () => {
      await nextTick();
      scrollToBottom(true);
      emit('messageReceived');
    },
    { deep: true }
  );
});

const fileInputRef = ref<HTMLInputElement | null>(null);

function openFilePicker() {
  fileInputRef.value?.click();
}

function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const files = target.files;

  if (!files || files.length === 0) return;

  sendSnapshot(files[0]);
}

const newMessage = ref("");
const textareaRef = ref<HTMLTextAreaElement | null>(null);

function autoResizeTextarea() {
  if (!textareaRef.value) return;

  textareaRef.value.style.height = 'auto';

  const scrollHeight = textareaRef.value.scrollHeight;
  const lineHeight = 24;
  const maxLines = 4;
  const maxHeight = lineHeight * maxLines;

  if (scrollHeight > maxHeight) {
    textareaRef.value.style.height = `${maxHeight}px`;
    textareaRef.value.style.overflowY = 'auto';
  } else {
    textareaRef.value.style.height = `${scrollHeight}px`;
    textareaRef.value.style.overflowY = 'hidden';
  }
}

async function handleInput() {
  autoResizeTextarea();
  sendPreview(newMessage.value.trim());

  await nextTick();
  scrollToBottom(true);
}

function send() {
  if (!newMessage.value.trim()) return;

  sendMessage(newMessage.value.trim());
  newMessage.value = "";
  sendPreview("");

  nextTick(() => {
    if (textareaRef.value) {
      textareaRef.value.style.height = 'auto';
    }
  });
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    send();
  }
}

onUpdated(async () => {
  if (!initialScrollDone.value && !loading.value && messages.value.length > 0) {
    await nextTick();
    scrollToBottom(false);
    initialScrollDone.value = true;
  }
});

</script>

<template>
  <div class="chat-view">
    <PictureInPictureVideo
      v-if="cameraStream && videoIsAllowed"
      :stream="cameraStream"
      @click="emit('pictureInPictureVideoClicked')"
    />

    <div class="chat-main">
      <div ref="containerRef" class="chat-messages" >
        <div class="chat-messages__spacer"></div>

        <div v-if="loading" class="loading">
          <p>{{ t('chat.loading') }}</p>
        </div>

        <template v-else>
          <ChatMessage
            v-for="(m, index) in messages"
            :key="m.id ?? index"
            :message="m"
            :translationsAllowed="translationsAllowed"
            :type="m.userType === ChatUserType.Caller ? 'sent' : 'received'"
            :delivery-status="'delivered'"
          />

          <div
            v-if="dispatcherChatTextMessagePreview"
            class="chat-message--received chat-message--pending"
            data-testid="chat-dispatcher-typing"
          >
            <span class="chat-message__status animate-fade">
              {{ t('chat.dispatcherIsTyping') }}
            </span>
            <p class="chat-message__text">
              {{ dispatcherChatTextMessagePreview }}
            </p>
          </div>

          <UploadingMessage
            v-for="upload in pendingUploads"
            :key="upload.id"
            :upload="upload"
          />

          <div ref="endRef" class="scroll-anchor"></div>
        </template>
      </div>

      <div class="chat-input">
        <BaseCircleButton
          icon="attachment"
          variant="secondary"
          :aria-label="t('chat.attachFile')"
          :disabled="isUploading"
          data-testid="chat-attach-button"
          @click="openFilePicker"
        />
        <input
          ref="fileInputRef"
          type="file"
          style="display: none"
          @change="handleFileChange"
        />
        <textarea
          ref="textareaRef"
          v-model="newMessage"
          @keydown="handleKeydown"
          @input="handleInput"
          :placeholder="t('chat.inputPlaceholder')"
          rows="1"
          data-testid="chat-message-input"
        ></textarea>
        <BaseCircleButton
          icon="up"
          variant="primary"
          :aria-label="t('chat.sendMessage')"
          data-testid="chat-send-button"
          @click="send"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.chat-view {
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background-color: var(--color-chat-background);
}

.chat-main {
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-height: 0;
  z-index: 0;
}

.chat-messages {
  position: relative;
  z-index: 0;
  width: 100%;
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  overflow-y: auto;
  scroll-behavior: smooth;
}

.chat-messages__spacer {
  flex-grow: 1;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--color-text);
}

.scroll-anchor {
  height: 1px;
  flex: 0 0 auto;
}

.chat-input {
  display: flex;
  align-items: flex-end;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  background-color: var(--color-chat-background);
  min-height: 56px;
}

.chat-input textarea {
  flex: 1;
  min-height: 40px;
  max-height: 96px;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  font-size: var(--font-size-md);
  font-family: inherit;
  line-height: 1.5;
  resize: none;
  overflow-y: hidden;
  transition: height 0.1s ease;
}

.chat-input textarea:focus {
  outline: none;
  border-color: var(--color-primary);
}

.chat-input textarea::placeholder {
  color: var(--color-text-label);
}

.chat-message--pending {
  border: var(--border-width) dashed var(--color-border);
  border-radius: 8px;
  border-bottom-left-radius: 0px;
  padding: var(--spacing-xs);
  background: transparent;
}

</style>
